import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, Globe, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UPDATE_USER_LANGUAGE } from '@modules/users/userQueries';
import { type Language, useLocale } from '@shared/hooks/locale';
import { languagesList } from '@shared/LocaleContext';

type EditableLanguageRowProps = {
  userId: string;
  language: string | null | undefined;
};

export const EditableLanguageRow = ({
  userId,
  language,
}: EditableLanguageRowProps) => {
  const { t, locale } = useLocale();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [updateUserLanguage, { loading, error, reset }] =
    useMutation(UPDATE_USER_LANGUAGE);

  const displayNames = useMemo(
    () => new Intl.DisplayNames([locale], { type: 'language' }),
    [locale],
  );

  const languageOptions = languagesList.map(code => ({
    code,
    label: displayNames.of(code) ?? code,
  }));

  const currentLabel = language ? displayNames.of(language) || language : null;

  const handleSelect = (next: Language | null) => {
    setMenuAnchor(null);
    void updateUserLanguage({
      variables: { id: userId, language: next },
    }).catch(() => {});
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 28,
          opacity: loading ? 0.5 : 1,
        }}
      >
        <Box
          sx={{
            color: 'text.secondary',
            display: 'flex',
            flexShrink: 0,
            width: 16,
          }}
        >
          <Globe size={16} />
        </Box>

        {language == null ? (
          <Typography
            component="button"
            type="button"
            onClick={evt => setMenuAnchor(evt.currentTarget)}
            variant="body2"
            sx={{
              color: 'primary.main',
              background: 'none',
              border: 'none',
              p: 0,
              cursor: 'pointer',
              font: 'inherit',
              textAlign: 'left',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('Set language')}
          </Typography>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', flex: 1, minWidth: 0 }}
              noWrap
            >
              {currentLabel}
            </Typography>
            <Tooltip title={t('Edit language')}>
              <IconButton
                size="small"
                onClick={evt => setMenuAnchor(evt.currentTarget)}
                sx={{
                  width: 24,
                  height: 24,
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <MoreHorizontal size={14} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <Pencil size={14} />
          </ListItemIcon>
          <ListItemText>{t('Change language')}</ListItemText>
        </MenuItem>
        {languageOptions.map(option => (
          <MenuItem
            key={option.code}
            onClick={() => handleSelect(option.code)}
            selected={option.code === language}
          >
            <ListItemIcon>
              {option.code === language ? (
                <Check size={14} />
              ) : (
                <Box sx={{ width: 14 }} />
              )}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
        {language != null && (
          <MenuItem
            onClick={() => handleSelect(null)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'error.main' }}>
              <Trash2 size={14} />
            </ListItemIcon>
            <ListItemText>{t('Clear language')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </>
  );
};
